import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  assignRequirementAction,
  createProjectCommunicationDraft,
  createProjectFollowUp,
  getGateWorkflow,
  getProjectOperations,
  listProjectInsightsMessages,
  updateGateDeliverable,
  type CommunicationDraft,
  type GateDeliverable,
  type GateWorkflow,
  type ProjectFollowUp,
  type ProjectOperations,
  type RequirementSourceRef,
  type SelaDecisionBriefNextAction,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { recordV2ErrorStage, recordV2FunnelStep } from '../../utils/v2Rum';
import { SelaDocumentUploadButton } from './SelaDocumentUploadButton';
import {
  AssignRequirementActionModal,
  type AssignRequirementActionValues,
} from './SelaProjectOperationModals';
import { SelaProviderSearchModal } from './SelaProviderSearchModal';
import { useAskSela } from './ask-sela/AskSelaContext';

type WorkspaceContext = {
  project: SelaProjectDetail;
};

const EMPTY_WORKFLOW: GateWorkflow = {
  workflowStatus: 'pending_decision_brief',
  statusMessage: 'Diligence Roadmap will be generated after the Decision Brief is complete.',
  gates: [],
  stageProfiles: [],
  deliverables: [],
  approvals: [],
  blockers: [],
  dependencies: [],
  timelineEvents: [],
};

const EMPTY_OPERATIONS: ProjectOperations = {
  people: [],
  roleNeeds: [],
  suggestedRoleNeeds: [],
  followUps: [],
  drafts: [],
  summary: {
    peopleCount: 0,
    openFollowUpCount: 0,
    overdueFollowUpCount: 0,
    waitingOnDocumentCount: 0,
    unassignedRoleNeedCount: 0,
  },
};

const STATUS_LABEL: Record<string, string> = {
  locked: 'Locked',
  active: 'Active',
  ready: 'Ready',
  complete: 'Complete',
  blocked: 'Blocked',
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting on someone',
  waiting_on_someone: 'Waiting on someone',
  waiting_on_document: 'Waiting on document',
  resolved: 'Resolved',
  waived: 'Waived',
  missing: 'Missing',
  not_started: 'Not started',
  needs_review: 'Needs review',
  not_applicable: 'Not applicable',
  skipped: 'Skipped',
};

const STATUS_TONE: Record<string, string> = {
  locked: 'bg-slate-100 text-slate-500',
  active: 'bg-blue-50 text-blue-700',
  ready: 'bg-emerald-50 text-emerald-700',
  complete: 'bg-emerald-50 text-emerald-700',
  blocked: 'bg-rose-50 text-rose-700',
  open: 'bg-rose-50 text-rose-700',
  in_progress: 'bg-blue-50 text-blue-700',
  waiting: 'bg-amber-50 text-amber-700',
  waiting_on_someone: 'bg-amber-50 text-amber-700',
  waiting_on_document: 'bg-orange-50 text-orange-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  waived: 'bg-slate-100 text-slate-600',
  missing: 'bg-amber-50 text-amber-700',
  not_started: 'bg-slate-100 text-slate-600',
  needs_review: 'bg-amber-50 text-amber-700',
  not_applicable: 'bg-slate-100 text-slate-600',
  skipped: 'bg-slate-100 text-slate-600',
};

const REQUIREMENT_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_on_someone', label: 'Waiting on someone' },
  { value: 'waiting_on_document', label: 'Waiting on document' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'complete', label: 'Complete' },
  { value: 'skipped', label: 'Skipped / accepted risk' },
  { value: 'not_applicable', label: 'Not applicable' },
];

const FOLLOW_UP_STATUS_LABEL: Record<string, string> = {
  draft_needed: 'Draft needed',
  draft_ready: 'Draft ready',
  sent: 'Sent',
  waiting_on_response: 'Waiting on response',
  waiting_on_document: 'Waiting on document',
  needs_user_review: 'Needs review',
  completed: 'Completed',
  closed: 'Closed',
  blocked: 'Blocked',
};

const OPEN_FOLLOW_UP_STATUSES = new Set([
  'draft_needed',
  'draft_ready',
  'sent',
  'waiting_on_response',
  'waiting_on_document',
  'needs_user_review',
  'blocked',
]);

const COMPLETED_REQUIREMENT_STATUSES = new Set(['complete', 'skipped', 'not_applicable']);

const PATH_LABELS: Record<string, string> = {
  adu_dadu: 'ADU / DADU',
  remodel_addition: 'Remodel / Addition',
  scrape_rebuild: 'Scrape & Rebuild',
  single_family_home: 'Single Family Home',
  small_multifamily_townhomes: 'Small Multifamily / Townhomes',
  short_plat_subdivision: 'Short Plat / Subdivision',
  buy_hold_rental: 'Buy & Hold / Rental',
  recommend_paths: 'Not sure - recommend paths',
};

function labelForStatus(status?: string): string {
  return STATUS_LABEL[status || ''] || (status || '').replace(/_/g, ' ') || 'Unknown';
}

function shortDate(value?: string | null): string {
  if (!value) return 'No due date';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return value;
  return new Date(timestamp).toLocaleDateString();
}

function isOpenFollowUp(followUp: ProjectFollowUp): boolean {
  return OPEN_FOLLOW_UP_STATUSES.has(followUp.status);
}

function isCompletedRequirement(status?: string): boolean {
  return COMPLETED_REQUIREMENT_STATUSES.has(status || '');
}

function followUpLabel(status?: string): string {
  return FOLLOW_UP_STATUS_LABEL[status || ''] || (status || '').replace(/_/g, ' ') || 'Not started';
}

function deliverableSourceRef(deliverable: GateDeliverable, stageId?: string): RequirementSourceRef {
  return {
    sourceType: 'gate_deliverable',
    sourceId: deliverable.deliverableId,
    label: deliverable.label,
    stageId,
    gateId: deliverable.gateId,
    deliverableId: deliverable.deliverableId,
    requirementId: deliverable.requirementId || deliverable.deliverableId,
  };
}

function followUpMatchesRef(followUp: ProjectFollowUp, ref: RequirementSourceRef): boolean {
  const itemRef = followUp.requirementRef;
  if (!itemRef || itemRef.sourceType !== ref.sourceType) return false;
  if (itemRef.deliverableId && ref.deliverableId && itemRef.deliverableId === ref.deliverableId) return true;
  if (itemRef.requirementId && ref.requirementId && itemRef.requirementId === ref.requirementId) return true;
  return Boolean(itemRef.sourceId && ref.sourceId && itemRef.sourceId === ref.sourceId);
}

function RequirementDetail({ label, children }: { label: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">{label}</div>
      <div className="mt-1 text-sm leading-relaxed text-[#334155]">{children}</div>
    </div>
  );
}

export function SelaProjectGateBoardPage() {
  const { project } = useOutletContext<WorkspaceContext>();
  const { openAskSela } = useAskSela();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = project.id;
  const appliedDeepLinkRef = useRef(false);
  const [highlightDeliverableId, setHighlightDeliverableId] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<GateWorkflow>(EMPTY_WORKFLOW);
  const [operations, setOperations] = useState<ProjectOperations>(EMPTY_OPERATIONS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<{ deliverable: GateDeliverable; sourceRef: RequirementSourceRef } | null>(null);
  const [providerSearchTarget, setProviderSearchTarget] = useState<{ deliverable: GateDeliverable; sourceRef: RequirementSourceRef } | null>(null);
  const [draftingSourceId, setDraftingSourceId] = useState<string | null>(null);
  const [draftPreview, setDraftPreview] = useState<{
    draft?: CommunicationDraft;
    followUp?: ProjectFollowUp;
    actionLabel: string;
    isDrafting: boolean;
  } | null>(null);
  const [requirementStatusTarget, setRequirementStatusTarget] = useState<GateDeliverable | null>(null);
  const [selectedGateId, setSelectedGateId] = useState<string | null>(null);
  const [expandedRequirementIds, setExpandedRequirementIds] = useState<Set<string>>(new Set());
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [insightActionByDeliverableId, setInsightActionByDeliverableId] = useState<Record<string, SelaDecisionBriefNextAction>>({});

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [next, nextOperations, insightMessages] = await Promise.all([
        getGateWorkflow(projectId),
        getProjectOperations(projectId),
        listProjectInsightsMessages(projectId).catch(() => []),
      ]);
      setWorkflow(next);
      setOperations(nextOperations);
      const latestBrief = [...insightMessages]
        .reverse()
        .find((message) => message.status === 'complete' && message.decisionBrief)?.decisionBrief;
      const priorityMap: Record<string, SelaDecisionBriefNextAction> = {};
      for (const action of latestBrief?.nextActions || []) {
        const deliverableId = action.roadmapRef?.deliverableId;
        if (deliverableId) {
          priorityMap[deliverableId] = action;
        }
      }
      setInsightActionByDeliverableId(priorityMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roadmap.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const onPointerDown = (event: PointerEvent) => {
      const root = document.querySelector(`[data-roadmap-more-menu="${openActionMenuId}"]`);
      if (root && !root.contains(event.target as Node)) {
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [openActionMenuId]);

  const defaultGateId = useMemo(() => {
    return workflow.summary?.currentStageId
      || workflow.gates.find((gate) => ['blocked', 'in_progress', 'needs_review'].includes(gate.status))?.gateId
      || workflow.gates[0]?.gateId
      || null;
  }, [workflow.gates, workflow.summary?.currentStageId]);

  useEffect(() => {
    if (!workflow.gates.length) return;
    if (selectedGateId && workflow.gates.some((gate) => gate.gateId === selectedGateId)) return;
    setSelectedGateId(defaultGateId);
  }, [defaultGateId, selectedGateId, workflow.gates]);

  useEffect(() => {
    if (loading || !workflow.gates.length || appliedDeepLinkRef.current) return;
    const deliverableId = searchParams.get('deliverableId');
    const gateId = searchParams.get('gateId');
    if (!deliverableId && !gateId) return;

    appliedDeepLinkRef.current = true;
    let targetGateId = gateId;
    if (!targetGateId && deliverableId) {
      targetGateId = workflow.deliverables.find((item) => item.deliverableId === deliverableId)?.gateId || null;
    }
    if (targetGateId && workflow.gates.some((gate) => gate.gateId === targetGateId)) {
      setSelectedGateId(targetGateId);
    }
    if (deliverableId) {
      setExpandedRequirementIds((current) => new Set(current).add(deliverableId));
      setHighlightDeliverableId(deliverableId);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('gateId');
    nextParams.delete('deliverableId');
    setSearchParams(nextParams, { replace: true });
  }, [loading, searchParams, setSearchParams, workflow.deliverables, workflow.gates]);

  const selectedGate = useMemo(() => {
    return workflow.gates.find((gate) => gate.gateId === selectedGateId)
      || workflow.gates.find((gate) => gate.gateId === defaultGateId)
      || workflow.gates[0]
      || null;
  }, [defaultGateId, selectedGateId, workflow.gates]);

  const { selectedRequirements, completedSectionStartsAt } = useMemo(() => {
    if (!selectedGate) {
      return { selectedRequirements: [] as GateDeliverable[], completedSectionStartsAt: -1 };
    }
    const stageItems = workflow.deliverables.filter(
      (deliverable) => deliverable.gateId === selectedGate.gateId,
    );
    const open = stageItems.filter((deliverable) => !isCompletedRequirement(deliverable.status));
    const completed = stageItems.filter((deliverable) => isCompletedRequirement(deliverable.status));
    return {
      selectedRequirements: [...open, ...completed],
      completedSectionStartsAt: completed.length === 0 ? -1 : open.length === 0 ? 0 : open.length,
    };
  }, [selectedGate, workflow.deliverables]);

  useEffect(() => {
    if (!highlightDeliverableId || loading) return;
    const element = document.querySelector(`[data-roadmap-deliverable-id="${highlightDeliverableId}"]`);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setHighlightDeliverableId(null), 4000);
    return () => window.clearTimeout(timer);
  }, [highlightDeliverableId, loading, selectedGateId, selectedRequirements]);

  const selectedGateIndex = selectedGate ? workflow.gates.findIndex((gate) => gate.gateId === selectedGate.gateId) : -1;
  const selectedSummary = selectedGate?.gateSummary;

  const updateRequirement = async (
    deliverable: GateDeliverable,
    status: string,
    extra: Partial<GateDeliverable> = {},
  ) => {
    setBusy(true);
    recordV2FunnelStep('roadmap_status_update', 'start', {
      deliverable_id: deliverable.deliverableId,
      status,
      source_page: 'roadmap',
    }, projectId);
    try {
      await updateGateDeliverable(projectId, deliverable.deliverableId, {
        status,
        ...extra,
      });
      await reload();
      recordV2FunnelStep('roadmap_status_update', 'success', {
        deliverable_id: deliverable.deliverableId,
        status,
        source_page: 'roadmap',
      }, projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update action.');
      recordV2FunnelStep('roadmap_status_update', 'failure', {
        deliverable_id: deliverable.deliverableId,
        status,
        source_page: 'roadmap',
      }, projectId);
      recordV2ErrorStage('roadmap_status_update', err, {}, projectId);
    } finally {
      setBusy(false);
    }
  };

  const updateRequirementFromModal = (deliverable: GateDeliverable, status: string, note: string) => {
    const extra: Partial<GateDeliverable> = {};
    if (status === 'skipped') extra.skipReason = note || 'Accepting risk for now';
    if (status === 'not_applicable') extra.notApplicableReason = note;
    if (status === 'complete') extra.completeNote = note;
    setRequirementStatusTarget(null);
    void updateRequirement(deliverable, status, extra);
  };

  const assignRoadmapItem = async (sourceRef: RequirementSourceRef, values: AssignRequirementActionValues) => {
    const person = operations.people.find((item) => item.personId === values.personId);
    setBusy(true);
    recordV2FunnelStep('roadmap_assign_person', 'start', {
      person_id: values.personId,
      source_page: 'roadmap',
    }, projectId);
    try {
      await assignRequirementAction(projectId, {
        personId: values.personId,
        responsibleRole: person?.roles?.[0],
        requirementRef: sourceRef,
        openAsk: sourceRef.label,
        dueDate: values.dueDate,
        notes: values.notes,
      });
      setAssignTarget(null);
      setOpenActionMenuId(null);
      setOperations(await getProjectOperations(projectId));
      recordV2FunnelStep('roadmap_assign_person', 'success', {
        person_id: values.personId,
        source_page: 'roadmap',
      }, projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign roadmap item.');
      recordV2FunnelStep('roadmap_assign_person', 'failure', { source_page: 'roadmap' }, projectId);
      recordV2ErrorStage('roadmap_assign_person', err, {}, projectId);
    } finally {
      setBusy(false);
    }
  };

  const draftEmailForAction = async (
    deliverable: GateDeliverable,
    sourceRef: RequirementSourceRef,
    existingFollowUp?: ProjectFollowUp,
  ) => {
    const sourceId = sourceRef.deliverableId || sourceRef.sourceId;
    const actionLabel = sourceRef.label || deliverable.label;
    setBusy(true);
    setDraftingSourceId(sourceId);
    setDraftPreview({ actionLabel, isDrafting: true });
    recordV2FunnelStep('roadmap_draft_email', 'start', { source_page: 'roadmap' }, projectId);
    try {
      const followUp = existingFollowUp || await createProjectFollowUp(projectId, {
        openAsk: deliverable.followUp?.what || sourceRef.label || deliverable.label,
        title: deliverable.followUp?.what || sourceRef.label || deliverable.label,
        responsibleRole: deliverable.followUp?.who || deliverable.suggestedExpert || deliverable.ownerRole,
        requestedDocument: deliverable.suggestedDocuments?.[0],
        dueDate: deliverable.followUp?.dueDate,
        requirementRef: sourceRef,
        status: 'draft_needed',
      });
      const result = await createProjectCommunicationDraft(projectId, followUp.followUpId);
      setDraftPreview({ draft: result.draft, followUp: result.followUp, actionLabel, isDrafting: false });
      setOpenActionMenuId(null);
      await reload();
      recordV2FunnelStep('roadmap_draft_email', 'success', {
        follow_up_id: followUp.followUpId,
        source_page: 'roadmap',
      }, projectId);
    } catch (err) {
      setDraftPreview(null);
      setError(err instanceof Error ? err.message : 'Failed to draft email.');
      recordV2FunnelStep('roadmap_draft_email', 'failure', { source_page: 'roadmap' }, projectId);
      recordV2ErrorStage('roadmap_draft_email', err, {}, projectId);
    } finally {
      setBusy(false);
      setDraftingSourceId(null);
    }
  };

  const roadmapPending = !loading && (!workflow.gates.length || workflow.workflowStatus === 'pending_decision_brief');

  const toggleRequirementDetails = (deliverableId: string) => {
    setExpandedRequirementIds((current) => {
      const next = new Set(current);
      if (next.has(deliverableId)) {
        next.delete(deliverableId);
      } else {
        next.add(deliverableId);
      }
      return next;
    });
  };

  const copyDraft = async (draft: CommunicationDraft) => {
    await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    setNotice('Draft copied.');
  };

  return (
    <main className="flex-1 overflow-auto p-6 md:p-8">
      <div className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">Diligence Roadmap</h1>
          <p className="mt-2 max-w-3xl text-sm text-[#647488]">
            Work through the actions, documents, expert questions, and risks that unblock each stage from evaluation toward permit and construction readiness.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      ) : null}

      {loading ? (
        <section className="rounded-xl border border-[#e1e8f0] bg-white p-6 text-sm text-[#647488] shadow-sm">
          Loading diligence roadmap...
        </section>
      ) : roadmapPending ? (
        <section className="rounded-xl border border-dashed border-[#cdd5e1] bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Roadmap status</p>
          <h2 className="mt-2 text-xl font-bold text-[#0f172a]">Building diligence roadmap</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#475669]">
            Sela will generate this roadmap after the Decision Brief is complete. The roadmap will apply stable diligence playbooks to the parcel, selected development paths, risks, missing facts, and required actions.
          </p>
          <div className="mt-5 grid gap-3 text-sm text-[#334155] md:grid-cols-2">
            {['Applying development-path playbooks', 'Mapping risks and missing facts', 'Creating action cards', 'Preparing stage gates'].map((item) => (
              <div key={item} className="rounded-lg border border-[#e1e8f0] bg-[#f8fafc] px-4 py-3">{item}</div>
            ))}
          </div>
        </section>
      ) : (
        <>
          <section className="mb-6 rounded-xl border border-[#e1e8f0] bg-white p-4 shadow-sm">
            <div className="overflow-x-auto px-1 pb-2 pt-3">
              <div className="relative flex min-w-max items-start gap-0">
                <div className="absolute left-8 right-8 top-[18px] h-0.5 bg-[#d9e2ef]" aria-hidden="true" />
                {workflow.gates.map((gate, index) => {
                  const isSelected = selectedGate?.gateId === gate.gateId;
                  const summary = gate.gateSummary;
                  const isComplete = gate.status === 'complete';
                  const isBlocked = gate.status === 'blocked';
                  const openCount = summary?.requiredUnresolved ?? 0;
                  const riskCount = summary?.skippedWithAcceptedRisk ?? 0;
                  return (
                    <button
                      key={gate.gateId}
                      type="button"
                      onClick={() => setSelectedGateId(gate.gateId)}
                      className="relative z-10 flex w-[178px] flex-col items-center px-2 text-center transition"
                    >
                      <span
                        className={[
                          'flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white text-xs font-bold shadow-sm transition',
                          isSelected ? 'border-[#3251b7] bg-[#3251b7] text-white ring-4 ring-[#dce4ff]' : '',
                          !isSelected && isBlocked ? 'border-rose-300 text-rose-700' : '',
                          !isSelected && isComplete ? 'border-emerald-300 text-emerald-700' : '',
                          !isSelected && !isBlocked && !isComplete ? 'border-[#cdd5e1] text-[#647488]' : '',
                        ].join(' ')}
                      >
                        {index + 1}
                      </span>
                      <span className="mt-2 text-xs font-semibold text-[#0f172a]">{gate.label}</span>
                      <span className={['mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_TONE[gate.status] || STATUS_TONE.not_started].join(' ')}>
                        {labelForStatus(gate.status)}
                      </span>
                      <span className="mt-1 text-[11px] text-[#647488]">
                        {openCount} open{riskCount ? ` - ${riskCount} risk` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {selectedGate ? (
            <section className="rounded-2xl border border-[#e1e8f0] bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">
                    Stage {selectedGateIndex + 1}
                  </div>
                  <h2 className="mt-1 text-lg font-bold text-[#0f172a]">{selectedGate.label}</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#647488]">{selectedGate.goal || selectedGate.description}</p>
                </div>
                <span className={['w-fit rounded-full px-2.5 py-1 text-xs font-semibold', STATUS_TONE[selectedGate.status] || STATUS_TONE.not_started].join(' ')}>
                  {labelForStatus(selectedGate.status)}
                </span>
              </div>

              <div className="mt-5 border-t border-[#e1e8f0] pt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Actions required to unblock this stage</h3>
              </div>

              <div className="mt-3 grid gap-3 xl:grid-cols-2">
                {selectedRequirements.map((deliverable, requirementIndex) => {
                  const sourceRef = deliverableSourceRef(deliverable, selectedGate.stageId);
                  const isCompleted = isCompletedRequirement(deliverable.status);
                  const actionFollowUps = operations.followUps.filter((followUp) => followUpMatchesRef(followUp, sourceRef));
                  const primaryFollowUp = actionFollowUps.find(isOpenFollowUp) || actionFollowUps[0];
                  const assignedPerson = primaryFollowUp
                    ? operations.people.find((person) => person.personId === primaryFollowUp.personId)
                    : undefined;
                  const dueDate = primaryFollowUp?.dueDate || deliverable.followUp?.dueDate;
                  const documentNeed = deliverable.suggestedDocuments?.[0] || deliverable.followUp?.what || '';
                  const insightAction = insightActionByDeliverableId[deliverable.deliverableId];
                  const isDraftingEmail = draftingSourceId === (sourceRef.deliverableId || sourceRef.sourceId);
                  const askSelaAboutAction = () => openAskSela({
                    type: 'requirement',
                    label: deliverable.label,
                    sourceId: deliverable.requirementId || deliverable.deliverableId,
                    sourcePage: 'roadmap',
                    summary: deliverable.whyItMatters || deliverable.howToResolve || selectedSummary?.recommendation || '',
                    metadata: {
                      status: deliverable.status,
                      gateId: deliverable.gateId,
                      stageId: selectedGate.stageId,
                      gateLabel: selectedGate.label,
                      importance: deliverable.importance,
                      ownerRole: deliverable.ownerRole,
                      suggestedDocuments: deliverable.suggestedDocuments,
                      suggestedExpert: deliverable.suggestedExpert,
                      primaryFollowUpId: primaryFollowUp?.followUpId,
                      primaryFollowUpStatus: primaryFollowUp?.status,
                      primaryFollowUpDueDate: primaryFollowUp?.dueDate,
                      assignedPersonId: assignedPerson?.personId,
                      assignedPersonName: assignedPerson?.name,
                      assignedPersonRoles: assignedPerson?.roles,
                      linkedFollowUpCount: actionFollowUps.length,
                      sourceRef,
                    },
                  }, `What should I do next for "${deliverable.label}"?`);
                  return (
                  <React.Fragment key={deliverable.deliverableId}>
                    {requirementIndex === completedSectionStartsAt ? (
                      <div className="col-span-full border-t border-[#e1e8f0] pt-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-[#647488]">
                          Completed ({selectedRequirements.length - completedSectionStartsAt})
                        </h3>
                      </div>
                    ) : null}
                  <article
                    data-roadmap-deliverable-id={deliverable.deliverableId}
                    className={[
                      'relative rounded-xl border p-4 transition-shadow',
                      insightAction
                        ? 'border-[#3251b7]/40 bg-[#f8faff] shadow-sm ring-1 ring-[#3251b7]/10'
                        : isCompleted
                          ? 'border-[#e1e8f0] bg-[#f8fafc]'
                          : 'border-[#e1e8f0] bg-[#fbfdff]',
                      highlightDeliverableId === deliverable.deliverableId
                        ? 'ring-2 ring-[#3251b7] ring-offset-2'
                        : '',
                    ].join(' ')}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#647488]">
                          {insightAction ? 'Insight priority action' : 'Action item'}
                        </div>
                        <h3 className="text-sm font-bold text-[#0f172a]">{deliverable.label}</h3>
                        {insightAction?.reason ? (
                          <p className="mt-1 max-w-2xl text-xs font-medium leading-relaxed text-[#3251b7]">
                            {insightAction.reason}
                          </p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-[#647488]">
                          <span>Needed role: <b className="text-[#334155]">{assignedPerson ? assignedPerson.roles?.[0] || deliverable.ownerRole : deliverable.ownerRole}</b></span>
                          {dueDate ? <span>Due: <b className="text-[#334155]">{shortDate(dueDate)}</b></span> : null}
                          {documentNeed ? <span>Document: <b className="text-[#334155]">{documentNeed}</b></span> : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span className={['rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_TONE[deliverable.status] || STATUS_TONE.not_started].join(' ')}>
                          {labelForStatus(deliverable.status)}
                        </span>
                      </div>
                    </div>

                    {expandedRequirementIds.has(deliverable.deliverableId) ? (
                      <div className="mt-4 space-y-3 rounded-lg border border-[#e1e8f0] bg-white px-3 py-3">
                        <RequirementDetail label="Assigned person or needed role">
                          {assignedPerson
                            ? `${assignedPerson.name}${assignedPerson.roles?.[0] ? ` - ${assignedPerson.roles[0]}` : ''}`
                            : deliverable.ownerRole}
                        </RequirementDetail>
                        <RequirementDetail label="Why it matters">{deliverable.whyItMatters}</RequirementDetail>
                        <RequirementDetail label="How to resolve">{deliverable.howToResolve}</RequirementDetail>
                        <RequirementDetail label="Applies to">
                          {(deliverable.appliesToDevelopmentPaths?.length ? deliverable.appliesToDevelopmentPaths : project.developmentPaths).map((path) => PATH_LABELS[path] || path).join(', ') || 'All selected paths'}
                        </RequirementDetail>
                        {deliverable.suggestedDocuments?.length ? (
                          <RequirementDetail label="Recommended documents">{deliverable.suggestedDocuments.join(', ')}</RequirementDetail>
                        ) : null}
                        {deliverable.suggestedExpert ? (
                          <RequirementDetail label="Suggested expert">
                            {deliverable.suggestedExpert}
                            {deliverable.expertQuestions?.length ? ` - ${deliverable.expertQuestions[0]}` : ''}
                          </RequirementDetail>
                        ) : null}
                        {deliverable.relatedRiskOrMissingFact ? (
                          <RequirementDetail label="Related risk">{deliverable.relatedRiskOrMissingFact}</RequirementDetail>
                        ) : null}
                        {deliverable.followUp?.who || deliverable.followUp?.what ? (
                          <RequirementDetail label="Suggested follow-up">
                            {[deliverable.followUp?.who, deliverable.followUp?.what, deliverable.followUp?.dueDate].filter(Boolean).join(' - ')}
                          </RequirementDetail>
                        ) : null}
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Follow-ups</div>
                          {actionFollowUps.length ? (
                            <div className="mt-2 space-y-2">
                              {actionFollowUps.map((followUp) => {
                                const person = operations.people.find((item) => item.personId === followUp.personId);
                                return (
                                  <div key={followUp.followUpId} className="rounded-lg border border-[#e1e8f0] bg-[#f8fafc] px-3 py-2 text-xs text-[#475669]">
                                    <div className="font-semibold text-[#0f172a]">{followUp.openAsk}</div>
                                    <div className="mt-1 flex flex-wrap gap-2">
                                      <span>{person?.name || followUp.responsibleRole || 'Unassigned'}</span>
                                      <span>{followUpLabel(followUp.status)}</span>
                                      <span>Due {shortDate(followUp.dueDate)}</span>
                                      {followUp.lastContactedAt ? <span>Last contacted {shortDate(followUp.lastContactedAt)}</span> : null}
                                    </div>
                                    {followUp.notes ? <div className="mt-1 text-[#647488]">{followUp.notes}</div> : null}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="mt-1 text-sm text-[#647488]">No follow-ups yet.</div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-[#647488]">
                          <span>{deliverable.documentIds.length} uploaded evidence document{deliverable.documentIds.length === 1 ? '' : 's'} attached</span>
                          {deliverable.evidenceNodeIds.length ? <span>{deliverable.evidenceNodeIds.length} evidence fact{deliverable.evidenceNodeIds.length === 1 ? '' : 's'}</span> : null}
                          {deliverable.timelineExposure ? <span>Timing exposure: {deliverable.timelineExposure}</span> : null}
                          {deliverable.softCostExposure ? <span>Soft-cost exposure: {deliverable.softCostExposure}</span> : null}
                          {deliverable.status === 'skipped' && deliverable.skipReason ? <span>Accepted risk: {deliverable.skipReason}</span> : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => toggleRequirementDetails(deliverable.deliverableId)}
                        className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
                      >
                        {expandedRequirementIds.has(deliverable.deliverableId) ? 'Hide details' : 'Details'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRequirementStatusTarget(deliverable)}
                        disabled={busy}
                        className="rounded-md bg-[#3251b7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#28449f] disabled:opacity-50"
                      >
                        Update status
                      </button>
                      {!isCompleted ? (
                        <button
                          type="button"
                          onClick={() => setProviderSearchTarget({ deliverable, sourceRef })}
                          disabled={busy}
                          className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] disabled:opacity-50"
                        >
                          Find providers
                        </button>
                      ) : null}
                      <div
                        className="relative inline-block text-left"
                        data-roadmap-more-menu={deliverable.deliverableId}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenActionMenuId((current) => (current === deliverable.deliverableId ? null : deliverable.deliverableId))}
                          disabled={busy}
                          aria-expanded={openActionMenuId === deliverable.deliverableId}
                          aria-haspopup="menu"
                          className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] disabled:opacity-50"
                        >
                          More
                        </button>
                        {openActionMenuId === deliverable.deliverableId ? (
                          <div
                            className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] rounded-lg border border-[#d8e0eb] bg-white p-1 text-xs shadow-lg"
                            role="menu"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setAssignTarget({ deliverable, sourceRef });
                                setOpenActionMenuId(null);
                              }}
                              className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
                            >
                              {primaryFollowUp ? 'Reassign person' : 'Assign person'}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setOpenActionMenuId(null);
                                void draftEmailForAction(deliverable, sourceRef, primaryFollowUp);
                              }}
                              disabled={isDraftingEmail}
                              className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc] disabled:cursor-wait disabled:opacity-60"
                            >
                              {isDraftingEmail ? 'Drafting email...' : 'Draft email'}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                askSelaAboutAction();
                                setOpenActionMenuId(null);
                              }}
                              className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
                            >
                              Ask Sela
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                  </React.Fragment>
                  );
                })}
              </div>

            </section>
          ) : null}
        </>
      )}

      {assignTarget ? (
        <AssignRequirementActionModal
          title={operations.followUps.some((followUp) => followUpMatchesRef(followUp, assignTarget.sourceRef)) ? 'Reassign roadmap item' : 'Assign roster member'}
          description={`Assign ${assignTarget.sourceRef.label || 'this action'} to the roster member responsible for moving it forward.`}
          people={operations.people.filter((person) => person.status !== 'archived')}
          existingAction={operations.followUps.find((followUp) => followUpMatchesRef(followUp, assignTarget.sourceRef))}
          busy={busy}
          onClose={() => setAssignTarget(null)}
          onSubmit={(values) => void assignRoadmapItem(assignTarget.sourceRef, values)}
        />
      ) : null}

      {providerSearchTarget ? (
        <SelaProviderSearchModal
          project={project}
          deliverable={providerSearchTarget.deliverable}
          sourceRef={providerSearchTarget.sourceRef}
          onClose={() => setProviderSearchTarget(null)}
        />
      ) : null}

      {draftPreview ? (
        <RoadmapDraftEmailModal
          draft={draftPreview.draft}
          followUp={draftPreview.followUp}
          actionLabel={draftPreview.actionLabel}
          isDrafting={draftPreview.isDrafting}
          onClose={() => setDraftPreview(null)}
          onCopy={(draft) => void copyDraft(draft)}
        />
      ) : null}

      {requirementStatusTarget ? (
        <UpdateRequirementStatusModal
          requirement={requirementStatusTarget}
          projectId={projectId}
          stageId={selectedGate?.stageId}
          busy={busy}
          onClose={() => setRequirementStatusTarget(null)}
          onSubmit={(status, note) => updateRequirementFromModal(requirementStatusTarget, status, note)}
          onEvidenceUploaded={async () => {
            await updateGateDeliverable(projectId, requirementStatusTarget.deliverableId, { status: 'complete' });
            setRequirementStatusTarget(null);
            await reload();
          }}
          onError={setError}
        />
      ) : null}

    </main>
  );
}

function UpdateRequirementStatusModal({
  requirement,
  projectId,
  stageId,
  busy,
  onClose,
  onSubmit,
  onEvidenceUploaded,
  onError,
}: {
  requirement: GateDeliverable;
  projectId: string;
  stageId?: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (status: string, note: string) => void;
  onEvidenceUploaded: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [status, setStatus] = useState(
    REQUIREMENT_STATUS_OPTIONS.some((option) => option.value === requirement.status)
      ? requirement.status
      : 'in_progress',
  );
  const [note, setNote] = useState(
    status === 'skipped'
      ? requirement.skipReason || 'Accepting risk for now'
      : status === 'not_applicable'
        ? requirement.notApplicableReason || ''
        : requirement.completeNote || '',
  );

  const noteLabel = status === 'skipped'
    ? 'Reason / accepted risk'
    : status === 'not_applicable'
      ? 'Reason'
      : 'Note';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-[#0f172a]">Update status</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#647488]">{requirement.label}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            x
          </button>
        </div>

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-[#647488]">
          Status
        </label>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="mt-2 w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm text-[#0f172a]"
        >
          {REQUIREMENT_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[#647488]">
          {noteLabel}
        </label>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          className="mt-2 w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm text-[#0f172a]"
          placeholder="Optional context for this status change"
        />

        <div className="mt-4 rounded-lg border border-[#e1e8f0] bg-[#f8fafc] px-3 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">
            Evidence
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#647488]">
            Upload evidence here when the status change depends on a document. Uploading evidence will attach it to this action and mark the action complete.
          </p>
          <SelaDocumentUploadButton
            projectId={projectId}
            label="Upload evidence"
            busyLabel="Uploading..."
            metadata={{
              workflowContext: 'timeline',
              stageId,
              gateId: requirement.gateId,
              deliverableId: requirement.deliverableId,
              requirementId: requirement.requirementId || requirement.deliverableId,
              uploadReason: `Evidence for ${requirement.label}`,
              uploadedFor: requirement.label,
            }}
            onUploaded={async () => {
              await onEvidenceUploaded();
            }}
            onError={onError}
            className="mt-3 rounded-md border border-[#3251b7] bg-white px-3 py-1.5 text-xs font-semibold text-[#3251b7] hover:bg-[#eff2fe] disabled:opacity-60"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="button" disabled={busy} onClick={() => onSubmit(status, note)} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}

function RoadmapDraftEmailModal({
  draft,
  followUp,
  actionLabel,
  isDrafting,
  onClose,
  onCopy,
}: {
  draft?: CommunicationDraft;
  followUp?: ProjectFollowUp;
  actionLabel: string;
  isDrafting: boolean;
  onClose: () => void;
  onCopy: (draft: CommunicationDraft) => void;
}) {
  const title = draft?.subject || 'Drafting email...';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-2xl rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Draft email</p>
            <h2 className="mt-1 text-lg font-bold text-[#0f172a]">{title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#647488]">
              {actionLabel || followUp?.openAsk}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            x
          </button>
        </div>

        {followUp ? (
          <div className="mt-5 grid gap-3 text-sm md:grid-cols-2">
            <RequirementDetail label="Follow-up status">{followUpLabel(followUp.status)}</RequirementDetail>
            <RequirementDetail label="Due date">{shortDate(followUp.dueDate)}</RequirementDetail>
          </div>
        ) : null}

        <div className="mt-5 rounded-lg border border-[#d8e0eb] bg-white p-4">
          {isDrafting || !draft ? (
            <div className="rounded-lg bg-[#f8fafc] p-4 text-sm text-[#647488]">
              Drafting the email with the latest project context...
            </div>
          ) : (
            <>
              <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Subject</div>
                  <p className="mt-1 text-sm font-semibold text-[#1e4474]">{draft.subject}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(draft)}
                  className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white hover:bg-[#28449f]"
                >
                  Copy draft
                </button>
              </div>
              <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-[#f8fafc] p-4 text-xs leading-5 text-[#334155]">
                {draft.body}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

